import { Feature } from 'src/app/common/models/usermanagement.model';
import { PayloadServiceHelper } from '../orchestration/payload-service.helper';

/**
 * DropdownHandlersHelper
 *
 * Extracted from ChatbotexpandComponent. Holds the ribbon dropdown / base / weight
 * selection handlers (significance, confidence level, reference, base, weight and the
 * generic panel toggles).
 *
 * Follows the existing `comp: any` helper pattern — the component keeps thin wrapper
 * methods (bound in the template) that delegate here, so all call sites remain unchanged
 * and behavior is preserved.
 */
export class DropdownHandlersHelper {
  static async SampleSizeDropDownClick(comp: any, SelectedValue: string, block: any = null): Promise<void> {
    const target = block || comp;
    target.isPairWise = false;
    target.isReference = false;

    // Set default confidence level if it was 'None' and we are selecting a significance type
    if (SelectedValue !== 'None' && target.DefaultConfidenceLevel === 'None') {
      target.DefaultConfidenceLevel = comp.is_confidenceLevel.length > 0 ? comp.is_confidenceLevel[0]['key'] : '95%';
    }

    if (SelectedValue == 'Reference') {
      target.isReference = true;

      target.IndexData = comp.getLastLevelColumnNestingValues(target);
    } else {
      target.SignificanceVsReference = '';
      target.isReference = false;
      target.DefaultDataDropDown = 'Data';
      target.DefaultSampleSize = SelectedValue;

      if (SelectedValue === 'None') {
        target.DefaultConfidenceLevel = 'None';
      }

      // Specialized payload service call for "Previous Year", "Previous Period" or "None"
      const handled = PayloadServiceHelper.dispatchSpecialSignificance(
        comp, target, SelectedValue, () => { target.sampleSizeDropdown = false; }
      );
      if (handled) {
        return;
      }

      if (SelectedValue == 'None') {
        target.DefaultConfidenceLevel = 'None';
      } else if (SelectedValue == 'Pairwise') {
        target.DefaultConfidenceLevel =
          comp.is_confidenceLevel.length > 0
            ? comp.is_confidenceLevel[0]['key']
            : '95%';
        target.isPairWise = true;
      } else {
        target.DefaultConfidenceLevel =
          comp.is_confidenceLevel.length > 0
            ? comp.is_confidenceLevel[0]['key']
            : '95%';
      }
      target.sampleSizeDropdown = false;
      comp.createTable(true, Feature.Significance, false, target);
    }
  }

  static async confidenceDropDownClick(comp: any, SelectedValue: string, block: any = null): Promise<void> {
    const target = block || comp;
    target.DefaultConfidenceLevel = SelectedValue;
    target.confidenceLevelDropDown = false;
    target.DefaultDataDropDown = 'Data';

    // Specialized payload service call for "Previous Year", "Previous Period" or "None"
    const handled = PayloadServiceHelper.dispatchSpecialSignificance(comp, target, target.DefaultSampleSize);
    if (handled) {
      return;
    }

    // Ensure we trigger the payload API for 'Reference' when confidence level changes
    if (target.DefaultSampleSize === 'Reference') {
      PayloadServiceHelper.dispatchPayloadOrFallback(comp, target, () => comp.createTable(true, Feature.ConfidenceLevel, false, target));
      return;
    }

    comp.createTable(true, Feature.ConfidenceLevel, false, target);
  }

  static referenceDropDownClickIndexSignificance(comp: any, SelectedValue: string, block?: any): void {
    const target = block || comp;
    target.SignificanceVsReference = SelectedValue;
    target.DefaultConfidenceLevel =
      comp.is_confidenceLevel.length > 0
        ? comp.is_confidenceLevel[0]['key']
        : '95%';
    target.DefaultSampleSize = 'Reference';
    target.DefaultDataDropDown = 'Data';
    target.isReference = false;

    PayloadServiceHelper.dispatchPayloadOrFallback(comp, target, () => comp.createTable(true, Feature.Significance, false, target));
  }

  static referenceDropDownClick(comp: any, SelectedValue: string, block?: any): void {
    const target = block || comp;
    target.DefaultReferenceLevel = comp.replacePipe(SelectedValue);
    target.selectedIndex = SelectedValue;
    comp.createTable(false, Feature.Index, true, target);
  }

  static togglePanel(comp: any, SelectedDropDown: string, block?: any): void {
    const target = block || comp;
    if (SelectedDropDown == 'significance') {
      target.sampleSizeDropdown = !target.sampleSizeDropdown;
      target.isReference = false;
    } else if (SelectedDropDown == 'confidence') {
      target.confidenceLevelDropDown = !target.confidenceLevelDropDown;
    } else if (SelectedDropDown == 'reference') {
      target.referenceLevelDropDown = !target.referenceLevelDropDown;
    } else if (SelectedDropDown == 'weight') {
      target.weightDropDown = !target.weightDropDown;
    } else if (SelectedDropDown == 'Base') {
      target.baseDropDown = !target.baseDropDown;
    } else if (SelectedDropDown == 'datadropdown') {
      target.dataDropDown = !target.dataDropDown;
    }
  }

  static toggleActiveBlock(comp: any, block: string, chatBlock?: any): void {
    const target = chatBlock || comp;
    if (block === 'filtering') {
      target.isFilteringActive = true;
      target.isProfilingActive = false;
    } else if (block === 'profiling') {
      target.isFilteringActive = false;
      target.isProfilingActive = true;
    } else if (block === 'total_base') {
      target.isTotalBase = true;
      target.isAnswerBase = false;
      target.baseDropDown = false;
      target.DefaultBase = 'Total Base';

      PayloadServiceHelper.dispatchPayloadOrFallback(comp, target, () => comp.createTable(false, Feature.CrosstabOutput, false, chatBlock));
    } else if (block === 'answer_base') {
      if (!comp.enableAnswerBase) {
        return;
      }
      target.isTotalBase = false;
      target.isAnswerBase = true;
      target.baseDropDown = false;
      target.DefaultBase = 'Answer Base';

      PayloadServiceHelper.dispatchPayloadOrFallback(comp, target, () => comp.createTable(false, Feature.CrosstabOutput, false, chatBlock));
    }
  }

  static weightDropDownClick(comp: any, SelectedValue: string, selectedWeight: string, chatBlock?: any): void {
    const target = chatBlock || comp;
    target.DefaultWeight = SelectedValue;
    target.weightDropDown = !target.weightDropDown;
    target.selectedWeight = selectedWeight;

    PayloadServiceHelper.dispatchPayloadOrFallback(comp, target, () => comp.createTable(false, Feature.CrosstabOutput, false, chatBlock));
  }

    static populateWeightDropdown(comp: any): void {
    if (!comp.variableTabData || (Array.isArray(comp.variableTabData) && comp.variableTabData.length === 0)) {
      comp.variableTabData = comp.crossTabService.getVarTabData || [];
    }

    comp.WeightDropDownList = [];
    comp.weightFilter = {};

    comp.configWeights?.filter((x: any) => x.is_selected).forEach((x: any) => {
      let WeightedData = comp.variableTabData.filter(
        (a: any) => a.data_variable_code === x.variable_code
      );

      if (x.variable_label === 'Unweighted') {
        comp.WeightDropDownList.push({
          Label: x.variable_label,
          responseCode: x.variable_label,
          tooltip: x.variable_label,
        });

        comp.weightFilter[x.variable_label] = x.variable_label;

      } else if (x.variable_label.trim() === 'Boost Factor') {
        comp.WeightDropDownList.push({
          Label: x.variable_label,
          responseCode: x.variable_code,
          tooltip: x.variable_label,
        });

        comp.weightFilter[x.variable_label] = x.variable_code;

      } else {
        if (WeightedData.length > 0) {
          comp.WeightDropDownList.push({
            Label: WeightedData[0].Level1,
            responseCode: WeightedData[0].data_variable_code,
            tooltip: WeightedData[0].tooltip,
          });
          comp.weightFilter[WeightedData[0].Level1] = WeightedData[0].data_variable_code;
        } else {
          const nonSpecialCount = comp.WeightDropDownList.filter(
            (w: any) => w.Label !== 'Unweighted' && w.Label !== 'Boost Factor'
          ).length;
          const weightLabel = nonSpecialCount === 0 ? 'Primary Weight'
            : nonSpecialCount === 1 ? 'Secondary Weight'
              : `Weight ${nonSpecialCount + 1}`;
          comp.WeightDropDownList.push({
            Label: weightLabel,
            responseCode: x.variable_code,
            tooltip: x.variable_label,
          });
          comp.weightFilter[weightLabel] = x.variable_code;
        }
      }
    });

    if (comp.WeightDropDownList.length > 0 && comp.DefaultWeight == '') {
      const defaultWeightObj = comp.configWeights?.find((x: any) =>
        x.is_default === true ||
        x.is_active === true ||
        x.is_default_weight === true ||
        x.default === true
      );

      let matchedDropdown;
      if (defaultWeightObj) {
        matchedDropdown = comp.WeightDropDownList.find((w: any) => w.responseCode === defaultWeightObj.variable_code || (defaultWeightObj.variable_label === 'Unweighted' && w.Label === 'Unweighted'));
      }

      if (matchedDropdown) {
        comp.DefaultWeight = matchedDropdown.Label;
        comp.selectedWeight = matchedDropdown.responseCode;
      } else {
        let defaultFallback = null;
        defaultFallback = comp.WeightDropDownList?.find((w: any) => w.Label === 'Primary Weight' || w.responseCode?.toLowerCase() === 'weightb');
        if (defaultFallback) {
          comp.selectedWeight = defaultFallback.responseCode;
          comp.DefaultWeight = defaultFallback.Label;
        } else {
          const unweighted = comp.WeightDropDownList.find((w: any) => w.Label === 'Unweighted' || w.responseCode === 'Unweighted');
          if (unweighted) {
            comp.DefaultWeight = unweighted.Label;
            comp.selectedWeight = unweighted.responseCode;
          } else {
            comp.DefaultWeight = comp.WeightDropDownList[0].Label;
            comp.selectedWeight = comp.WeightDropDownList[0].responseCode;
          }
        }
      }
      comp._configService.weightLabel = comp.DefaultWeight;
    }
  }

}
